import { PageType } from "@/services/shared-models";
import { CommentEntry } from "@/services/user-services/models";
import { useDataStore, useFilterStore } from "@/stores";
import ReplyIcon from "@mui/icons-material/Reply";
import clsx from "clsx";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { nameToAvatar } from "../../nameToAvatar";
import CommentsForm from "../CommentsForm";

interface CommentProps {
    comment: CommentEntry;
    topLevel: boolean;
    pageType: PageType;
    pageSlug?: string;
}

const Comment = ({ comment, topLevel = true, pageType, pageSlug }: CommentProps) => {
    const activeVersionId = useFilterStore((state) => state.activeVersion);
    const versions = useDataStore((state) => state.versions);
    const activeVersion = useMemo(
        () => versions.find((version) => version.id === activeVersionId),
        [versions, activeVersionId]
    );

    const [formVisible, setFormVisible] = useState<boolean>(false);
    const [replies, setReplies] = useState<Array<CommentEntry>>(comment?.replies ?? []);

    const { t } = useTranslation("common");

    const onCommentSubmitted = (comment: CommentEntry) => {
        setFormVisible(false);
        setReplies([...replies, comment]);
    };

    const onCommentCancelled = () => setFormVisible(false);

    return (
        <div className="flex gap-6 mt-7">
            <div
                className={clsx([
                    "flex shrink-0 rounded-full w-16 h-16 place-content-center items-center text-xl font-bold",
                    comment.isAdmin ? "bg-primary-500 text-white" : "bg-primary-100 text-primary-500",
                ])}
            >
                {nameToAvatar(comment.username)}
            </div>
            <div className="flex flex-col w-full">
                <div
                    className={clsx([
                        "flex flex-col rounded-lg border border-[#e5e5e5] w-full text-sm font-medium",
                        comment.isAdmin && "border-t-4 border-t-primary-500",
                    ])}
                >
                    <div className="text-gray-900 p-4">{comment.commentBlob}</div>
                    <div className="bg-[#F7FAFF] border-t border-[#e5e5e5] italic rounded-b-lg flex">
                        {/* Posté le 12 décembre 2024 à 16:47 par Unknown User. */}
                        <div className="px-4 py-2">
                            {t("comments.postedOn", {
                                date: comment.commentDate,
                                time: comment.commentDate,
                                user: comment.username,
                                formatParams: {
                                    date: { year: "numeric", month: "long", day: "numeric" },
                                    time: { hour: "2-digit", minute: "2-digit" },
                                },
                            })}
                        </div>
                        {topLevel && activeVersion?.isDefault && (
                            <button
                                className={clsx([
                                    "flex text-sm/normal items-center px-3 py-1 text-primary-500",
                                    "hover:bg-primary-100 rounded-br-lg ml-auto disabled:opacity-50",
                                    formVisible ? "cursor-auto pointer-events-none" : "cursor-pointer",
                                ])}
                                onClick={() => setFormVisible(true)}
                                disabled={formVisible}
                            >
                                <ReplyIcon className="w-5 h-5 mr-2" />
                                {t("comments.buttons.reply")}
                            </button>
                        )}
                    </div>
                </div>

                <CommentsForm
                    open={formVisible}
                    replyTo={comment.id}
                    onSubmit={onCommentSubmitted}
                    onCancel={onCommentCancelled}
                    pageType={pageType}
                    pageSlug={pageSlug}
                />

                {replies?.map((reply) => (
                    <Comment key={reply.id} comment={reply} topLevel={false} pageType={pageType} pageSlug={pageSlug} />
                ))}
            </div>
        </div>
    );
};

export default Comment;
